import React from "react";
import { useQuery } from "@apollo/client";
import { Box, Container, Typography, Rating } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { GOOGLE_REVIEWS_QUERY } from "../graphql/googleReviews";

type GoogleReview = {
  id: string;
  reviewerName: string;
  rating: number;
  reviewText: string;
  reviewDate?: string | null;
};

type GoogleReviewsData = {
  googleReviews: GoogleReview[];
};

const GoogleLogo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: 6 }}>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function GoogleReviewsSection() {
  const { data, loading } = useQuery<GoogleReviewsData>(GOOGLE_REVIEWS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const reviews = data?.googleReviews ?? [];

  if (loading && !reviews.length) return null;
  if (!reviews.length) return null;

  return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        py: { xs: 6, md: 10 },
        borderTop: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 3,
              color: "text.secondary",
              fontSize: "0.72rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mb: 1,
            }}
          >
            <GoogleLogo />
            Google Reviews
          </Typography>

          <Typography
            component="h2"
            sx={{
              fontFamily: `"Bodoni Moda", serif`,
              fontWeight: 600,
              fontSize: { xs: "1.8rem", md: "2.4rem" },
              letterSpacing: 0.3,
              color: "#111",
            }}
          >
            What Our Customers Say
          </Typography>

          {/* Overall Rating */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <Rating
              value={5}
              readOnly
              precision={0.1}
              sx={{
                "& .MuiRating-iconFilled": { color: "#F4B942" },
                fontSize: "1.2rem",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              5.0 on Google
            </Typography>
          </Box>
        </Box>

        {/* Carousel */}
        <Box sx={{ position: "relative" }}>
          <Swiper
            modules={[Navigation, A11y, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            navigation
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            breakpoints={{
              600: { slidesPerView: 2 },
              900: { slidesPerView: 3 },
            }}
            style={{ paddingBottom: "8px" }}
          >
            {reviews.map((review) => (
              <SwiperSlide key={review.id}>
                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 3,
                    p: { xs: 3, md: 3.5 },
                    height: 280,
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid rgba(0,0,0,0.07)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                    transition: "box-shadow 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 30px rgba(0,0,0,0.10)",
                    },
                  }}
                >
                  {/* Stars */}
                  <Rating
                    value={review.rating}
                    readOnly
                    size="small"
                    sx={{
                      "& .MuiRating-iconFilled": { color: "#F4B942" },
                      mb: 1.5,
                    }}
                  />

                  {/* Review Text */}
                  <Typography
                    sx={{
                      fontSize: "0.92rem",
                      lineHeight: 1.75,
                      color: "#333",
                      mb: 2.5,
                      flex: 1,
                      fontStyle: "italic",
                      display: "-webkit-box",
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    "{review.reviewText}"
                  </Typography>

                  {/* Footer */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderTop: "1px solid rgba(0,0,0,0.06)",
                      pt: 1.5,
                    }}
                  >
                    {/* Reviewer */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.2 }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          bgcolor: "#111",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          fontFamily: `"Bodoni Moda", serif`,
                          flexShrink: 0,
                        }}
                      >
                        {review.reviewerName.charAt(0).toUpperCase()}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.88rem",
                            color: "#111",
                            lineHeight: 1.2,
                          }}
                        >
                          {review.reviewerName}
                        </Typography>
                        {review.reviewDate && (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              mt: 0.2,
                            }}
                          >
                            {review.reviewDate}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Google Badge */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <GoogleLogo />
                    </Box>
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom arrow styles */}
          <style>{`
            .swiper-button-prev, .swiper-button-next {
              color: #111 !important;
              width: 20px !important;
              height: 20px !important;
            }
            .swiper-button-prev::after, .swiper-button-next::after {
              font-size: 16px !important;
            }
          `}</style>
        </Box>
      </Container>
    </Box>
  );
}
